import last from 'lodash/last'
import sumBy from 'lodash/sumBy'

import {
  FuneralPlanCurrentPlan,
  FuneralPlanMoneyEntry,
  FuneralPlanVersion,
  FuneralPlanPlanPart,
} from 'lib/models/funeral-plan'

/** What the payment page (2.14) builds from the versions the server sends:
 * the cards for the plan in place and the payment timeline. */

/** Not on the cards: a withdrawn or failed agreement, as agreementBadge treats
 * them, and a celebration's cancellation until it settles, so the celebration
 * keeps its card while its refund is due or has failed. Mirrors NOT_ON_CARDS in
 * the server's payment-page-state.ts. */
const NOT_ON_CARDS = ['cancelled', 'error', 'cancelling']

/** The versions the cards read: those in the plan, leaving out anything
 * NOT_ON_CARDS and a cancellation, which the page shows on its own. The server
 * never sends drafts. */
const cardVersionsOf = (versions: FuneralPlanVersion[]) =>
  versions.filter(
    ({ agreement, cancels }) =>
      !NOT_ON_CARDS.includes(agreement.status) && !cancels
  )

/** One card's worth of the plan: the versions paid one way, read from the
 * latest of them. None when no version was paid that way. */
const planPartOf = (
  versions: FuneralPlanVersion[],
  name: string,
  paymentType: FuneralPlanPlanPart['paymentType']
): FuneralPlanPlanPart | undefined => {
  const relevant = versions.filter(({ type }) => type === paymentType)
  const latest = last(relevant)
  if (!latest) return undefined
  const { payments, agreement, position } = latest

  const part: FuneralPlanPlanPart = {
    name,
    paymentType,
    /** What each agreement asked for, less any balance it carried in */
    priceInPence: sumBy(
      relevant,
      ({ stillToPayInPence, balanceIn }) =>
        stillToPayInPence - (balanceIn?.amountInPence ?? 0)
    ),
    stillToPayInPence: position.outstandingInPence,
    paidInFullOn: position.outstandingInPence <= 0 ? position.lastPaidOn : null,
  }
  if (paymentType !== 'Instalments') return part

  return {
    ...part,
    instalments: {
      monthlyPaymentInPence: payments.amountInPence,
      termMonths: agreement.planTermMonths,
      monthsLeft: position.remainingPayments,
      nextPaymentDueOn: position.nextPaymentDueOn,
    },
  }
}

/**
 * The plan in place, for the cards at the top of the page: one card for the
 * plan, paid the way its first agreement was, and one for a celebration paid
 * the other way when the server says it has one. A celebration paid the same
 * way as the plan is named alongside it on the plan's card. None until an
 * agreement is part of the plan.
 */
export const currentPlanOf = ({
  versions,
  planName,
  separateCelebration,
  celebrationName = null,
}: {
  versions: FuneralPlanVersion[]
  planName: string
  separateCelebration: boolean
  celebrationName?: string | null
}): FuneralPlanCurrentPlan | undefined => {
  const counted = cardVersionsOf(versions)
  const planType = counted[0]?.type
  const name = celebrationName ? `${planName} + ${celebrationName}` : planName
  const plan = planType && planPartOf(counted, name, planType)
  if (!plan) return undefined

  const celebration =
    separateCelebration &&
    planPartOf(
      counted,
      'Celebration',
      planType === 'Instalments' ? 'Upfront' : 'Instalments'
    )

  return { ...plan, ...(celebration && { celebration }) }
}

/** A row of the payment timeline */
export type PaymentTimelineRow =
  | (FuneralPlanMoneyEntry & {
      /** Money that belongs to no agreement is flagged */
      hasAgreement: boolean
    })
  | { kind: 'agreement'; date: string; version: FuneralPlanVersion }

/** Every payment, refund and agreement, newest first. On the same moment an
 * agreement sorts below its payments, as it was made first. */
export const timelineOf = ({
  versions,
  unlinkedMoney,
}: {
  versions: FuneralPlanVersion[]
  unlinkedMoney: FuneralPlanMoneyEntry[]
}): PaymentTimelineRow[] =>
  [
    ...unlinkedMoney.map((entry) => ({ ...entry, hasAgreement: false })),
    ...versions.flatMap((version): PaymentTimelineRow[] => [
      { kind: 'agreement', date: version.agreement.createdAt, version },
      ...version.money.map((entry) => ({ ...entry, hasAgreement: true })),
    ]),
  ].sort(
    (a, b) =>
      b.date.localeCompare(a.date) ||
      Number(a.kind === 'agreement') - Number(b.kind === 'agreement')
  )
